import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, User, X } from 'lucide-react';
import { ActionIcon, Badge, rem } from '@mantine/core';
import { useFloating, autoUpdate, offset, flip, shift, useClick, useDismiss, useRole, useInteractions, FloatingPortal } from '@floating-ui/react';
import { useUser } from '../contexts/UserContext';
import { formatDistanceToNow } from 'date-fns';
import { Comment } from '../types/todo';
import { SearchHighlight } from './SearchHighlight';
import './Comments.css'; // Restored for popup styling


interface CommentsProps {
  comments: Comment[];
  onAddComment?: (text: string) => Promise<void>;
  triggerOnly?: boolean;
  style?: React.CSSProperties;
  variant?: 'default' | 'inline'; // Add variant prop
  searchTerm?: string;
}

export const Comments: React.FC<CommentsProps> = ({ 
  comments = [], 
  onAddComment, 
  triggerOnly = false, 
  style, 
  variant = 'default',
  searchTerm
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { currentUser } = useUser();
  const commentsListRef = useRef<HTMLDivElement>(null);



  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [
      offset(8),
      flip({
        fallbackAxisSideDirection: "start",
      }),
      shift({ padding: 8 })
    ],
    whileElementsMounted: autoUpdate,
    placement: 'bottom-end'
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  // Auto-scroll to bottom when comments popup opens
  useEffect(() => {
    if (isOpen && commentsListRef.current) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        if (commentsListRef.current) {
          commentsListRef.current.scrollTop = commentsListRef.current.scrollHeight;
        }
      }, 150); // Increased delay to ensure DOM is ready
    }
  }, [isOpen]);

  // Separate effect for when comments change while already open
  useEffect(() => {
    if (isOpen && commentsListRef.current && comments.length > 0) {
      // Use requestAnimationFrame for smooth scrolling when comments are added
      requestAnimationFrame(() => {
        if (commentsListRef.current) {
          const scrollTarget = commentsListRef.current.scrollHeight;
          commentsListRef.current.scrollTo({
            top: scrollTarget,
            behavior: 'smooth'
          });
        }
      });
    }
  }, [comments, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || isSubmitting || !onAddComment) return;

    setIsSubmitting(true);
    try {
      await onAddComment(newComment.trim());
      setNewComment('');
      
      // Scroll to bottom after comment is added
      setTimeout(() => {
        if (commentsListRef.current) {
          commentsListRef.current.scrollTo({
            top: commentsListRef.current.scrollHeight,
            behavior: 'smooth'
          });
        }
      }, 100);
    } catch (error) {
      console.error('Failed to add comment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSubmit(e as React.FormEvent);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Unknown time';
    }
  };

  const getInitials = (email: string) => {
    return email.split('@')[0].slice(0, 2).toUpperCase();
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent click handlers (like edit todo modal)
    // Let floating-ui handle the toggle logic
    setIsOpen(!isOpen);
  };

  // Using Mantine components for styling - legacy functions removed

  const trigger = variant === 'inline' ? (
    <Badge
      ref={refs.setReference}
      variant="light"
      color="gray"
      leftSection={<MessageCircle size={12} />}
      size="sm"
      style={{ cursor: 'pointer', ...style }}
      {...getReferenceProps()}
      onClick={handleButtonClick}
      title={comments.length > 0 ? `${comments.length} comment${comments.length !== 1 ? 's' : ''}` : 'Add comment'}
    >
      {comments.length > 0 ? comments.length : 'Add'}
    </Badge>
  ) : (
    <ActionIcon
      ref={refs.setReference}
      variant="subtle"
      color="gray"
      size="sm"
      style={{ position: 'relative', ...style }}
      {...getReferenceProps()}
      onClick={handleButtonClick}
      title={`${comments.length} comment${comments.length !== 1 ? 's' : ''}`}
    >
      <MessageCircle size={16} />
      {comments.length > 0 && (
        <Badge
          size="xs"
          variant="filled"
          color="blue"
          style={{
            position: 'absolute',
            top: rem(-2),
            right: rem(-2),
            minWidth: rem(16),
            height: rem(16),
            padding: 0,
            fontSize: rem(10),
            lineHeight: rem(16)
          }}
        >
          {comments.length}
        </Badge>
      )}
    </ActionIcon>
  );

  return (
    <>
      {trigger}

      {!triggerOnly && isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="comments-popup"
            {...getFloatingProps()}
            onClick={(e) => e.stopPropagation()} // Prevent popup clicks from bubbling up
          >
            <div className="comments-header">
              <h3>Comments</h3>
              <button 
                className="comments-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close comments"
              >
                <X size={16} />
              </button>
            </div>

            <div className="comments-content">
              {/* Comments List */}
              <div className="comments-list" ref={commentsListRef}>
                {comments.length === 0 ? (
                  <div className="no-comments">
                    <MessageCircle size={32} />
                    <p>No comments yet</p>
                    <small>Be the first to add a comment!</small>
                  </div>
                ) : (
                  comments.map((comment) => (
                    <div key={comment.id} className="comment-item">
                      <div className="comment-avatar">
                        <User size={16} />
                        <span className="comment-initials">
                          {getInitials(comment.authorEmail)}
                        </span>
                      </div>
                      <div className="comment-content">
                        <div className="comment-header">
                          <span className="comment-author">
                            {comment.authorEmail.split('@')[0]}
                          </span>
                          <span className="comment-time">
                            {formatTimeAgo(comment.createdAt)}
                          </span>
                        </div>
                        <div className="comment-text">
                          <SearchHighlight text={comment.content} searchTerm={searchTerm || ''} />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Add Comment Form */}
              {currentUser && (
                <form className="add-comment-form" onSubmit={handleSubmit}>
                  <div className="comment-input-container">
                    <div className="comment-avatar comment-avatar-current">
                      <User size={16} />
                      <span className="comment-initials">
                        {getInitials(currentUser.email)}
                      </span>
                    </div>
                    <div className="comment-input-wrapper">
                      <textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Add a comment..."
                        className="comment-input"
                        rows={3}
                        disabled={isSubmitting}
                      />
                      <div className="comment-actions">
                        <small className="comment-hint">
                          {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+Enter to send
                        </small>
                        <button
                          type="submit"
                          className="comment-submit"
                          disabled={!newComment.trim() || isSubmitting}
                        >
                          <Send size={14} />
                          {isSubmitting ? 'Sending...' : 'Send'}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
};
